/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/order
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { graphqlMoneyBagField, graphqlMoneyBagFieldToRest } from "./constants";
import {
  ShopifyFulfillmentServiceKey,
  ShopifyOrderKey,
  ShopifyShippingLineKey,
  ShopifyShippingLineToFulfillmentServiceKey,
  ShopifyShippingLineToOrderKey,
  ShopifyShippingLineToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const currentDiscountedPriceSetKey = "ModelField-DataModel-Shopify-ShippingLine-currentDiscountedPriceSet";
const currentDiscountedPriceSetStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-ShippingLine-currentDiscountedPriceSet-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyShippingLineKey,
  "2022-01": {
    name: "Shopify Shipping Line",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title" } },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price" } },
      },
      {
        name: "Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "code" } },
      },
      {
        name: "Source",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "source" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyShippingLineToOrderKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "phone" } },
      },
      {
        name: "Requested Fulfillment Service",
        fieldType: FieldType.BelongsTo,
        key: ShopifyShippingLineToFulfillmentServiceKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requested_fulfillment_service_id" } },
        configuration: {
          relatedModelKey: ShopifyFulfillmentServiceKey,
        },
      },
      {
        name: "Delivery Category",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "delivery_category" } },
      },
      {
        name: "Carrier Identifier",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "carrier_identifier" } },
      },
      {
        name: "Discounted Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discounted_price" } },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price_set" } },
      },
      {
        name: "Discounted Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discounted_price_set" } },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discount_allocations" } },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyShippingLineToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "order" },
  },
  "2023-07": {
    fields: [
      {
        name: "Delivery Category",
        fieldType: FieldType.String,
        isDeleted: true,
      },
    ],
  },
  "2024-04": {
    fields: [
      {
        name: "Is Removed",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "is_removed",
          },
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id", availableInWebhook: ["orders/create", "orders/updated"] },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "title",
            graphQLFieldKey: "title",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "price",
            graphQLFieldKey: "price",
            skipGraphQLSelection: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Code",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "code",
            graphQLFieldKey: "code",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Source",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "source",
            graphQLFieldKey: "source",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyShippingLineToOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderId", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "phone",
            graphQLFieldKey: "phone",
            protectedCustomerData: ["phone"],
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Requested Fulfillment Service",
        fieldType: FieldType.BelongsTo,
        key: ShopifyShippingLineToFulfillmentServiceKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "requested_fulfillment_service_id",
            availableInWebhook: ["orders/create", "orders/updated"],
            graphQLFieldKey: "requestedFulfillmentService",
            graphQLSelection: "id",
            deprecated: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentServiceKey,
        },
      },
      {
        name: "Carrier Identifier",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "carrier_identifier",
            graphQLFieldKey: "carrierIdentifier",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Discounted Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "discounted_price",
            graphQLFieldKey: "discountedPrice",
            skipGraphQLSelection: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "price_set",
            availableInWebhook: ["orders/create", "orders/updated"],
            graphQLFieldKey: "originalPriceSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
          },
        },
      },
      {
        name: "Discounted Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "discounted_price_set",
            availableInWebhook: ["orders/create", "orders/updated"],
            graphQLFieldKey: "discountedPriceSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
          },
        },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "discount_allocations",
            availableInWebhook: ["orders/create", "orders/updated"],
            graphQLFieldKey: "discountAllocations",
            graphQLSelection: `
          allocatedAmountSet {
            shopMoney {
              amount
              currencyCode
            }
            presentmentMoney {
              amount
              currencyCode
            }
          }
          discountApplication {
            allocationMethod
            index
            targetSelection
            targetType
            value
          }
          `,
          },
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "tax_lines",
            availableInWebhook: ["orders/create", "orders/updated"],
            graphQLFieldKey: "taxLines",
            graphQLSelection: `
          channelLiable
          priceSet {
            shopMoney {
              amount
              currencyCode
            }
            presentmentMoney {
              amount
              currencyCode
            }
          }
          rate
          ratePercentage
          source
          title
          price
          `,
          },
        },
      },
      {
        name: "Delivery Category",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryCategory",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Is Removed",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "is_removed",
            graphQLFieldKey: "isRemoved",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Current Discounted Price Set",
        key: currentDiscountedPriceSetKey,
        initialStorageEpochKey: currentDiscountedPriceSetStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "currentDiscountedPriceSet",
            shopifyFieldKey: "current_discounted_price_set",
            availableInWebhook: ["orders/create", "orders/updated"],
            graphQLOnlyField: true,
            graphQLSelection: graphqlMoneyBagField,
          },
        },
      },
      {
        name: "Custom",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "custom",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Shipping Rate Handle",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "shippingRateHandle",
            graphQLOnlyField: true,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "orders",
      resource: "order.shippingLines",
    },
  },
};

export const ShopifyShippingLine = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
